import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { RadioButton, SwitchButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import {
    getProfileNotificationSettings,
    updateNotificationSettings
} from 'services/endpoints/settings/notifications/notificationSettings'
import { useNotificationSettingsStore } from 'store/notificationSettingsCacheStore'
import { type iNotificationsSettings } from 'types/iTypes'

import './_profileNotificationsModal.scss'

const CHANNELS = ['in_app', 'push', 'sms', 'email'] as const

type NotificationChannel = (typeof CHANNELS)[number]
type FlatChannelSettings = Record<string, boolean>

type GroupedChannelSettings = {
    model?: FlatChannelSettings
    subscriber?: FlatChannelSettings
}

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
    in_app: 'notifications:inApp',
    push: 'notifications:push',
    sms: 'notifications:sms',
    email: 'notifications:email'
}

const GLOBAL_SETTING_KEYS: Record<
    NotificationChannel,
    keyof iNotificationsSettings['notificationSettings']
> = {
    in_app: 'in_app_notifications_enabled',
    push: 'push_notifications_enabled',
    sms: 'sms_notifications_enabled',
    email: 'email_notifications_enabled'
}

const NOTIFICATION_LABELS: Record<string, string> = {
    new_comment: 'notifications:newComment',
    new_comment_like: 'notifications:newCommentLike',
    new_like: 'notifications:newLike',
    new_post: 'notifications:newPosts',
    new_subscriber: 'notifications:newSubscriber',
    new_tip: 'notifications:newTip',
    new_purchase_like: 'notifications:newPurchaseLike',
    new_premium_video_purchase: 'notifications:newPremiumVideoPurchase',
    new_message_purchase: 'notifications:newMessagePurchase',
    upcoming_stream_reminders: 'notifications:upcomingStreamReminders',
    subscription_renewal: 'notifications:subscriptionRenewal',
    new_renewal: 'notifications:newRenewal',
    new_referral: 'notifications:newReferral',
    new_replies: 'notifications:newReplies',
    expired_subscription: 'notifications:expiredSubscriptions',
    live_stream_invite: 'notifications:liveStreamInvite',
    stream_started: 'notifications:streamStarted',
    incoming_friend_request: 'notifications:incomingFriendRequest',
    accepted_friend_request: 'notifications:acceptedFriendRequest',
    new_tags: 'notifications:newTags',
    new_message: 'notifications:newMessages',
    monthly_newsletter: 'monthlyNewsletter',
    show_full_notification_email:
        'showFullTextOfTheMessagesInTheNotificationEmail',
    poll_responses: 'notifications:pollResponses'
}

interface ProfileNotificationsModalProps {
    id: string
}

interface UpdateNotificationPayload {
    other_user_id: string
    type: NotificationChannel
    [key: string]: string | boolean
}

const isGroupedChannelSettings = (
    settings:
        | FlatChannelSettings
        | GroupedChannelSettings
        | undefined
): settings is GroupedChannelSettings =>
    !!settings &&
    typeof settings === 'object' &&
    (
        Object.prototype.hasOwnProperty.call(settings, 'model') ||
        Object.prototype.hasOwnProperty.call(settings, 'subscriber')
    )

const ProfileNotificationsModal = ({
    id
}: ProfileNotificationsModalProps) => {
    const { t } = useTranslation()
    const modalData = useModalContext()
    const [activeTab, setActiveTab] =
        useState<NotificationChannel>('in_app')

    const profileSettings = useNotificationSettingsStore(
        state => state.profileSettings[id]
    )
    const setProfileSettings = useNotificationSettingsStore(
        state => state.setProfileSettings
    )
    const setProfileChannelSetting = useNotificationSettingsStore(
        state => state.setProfileChannelSetting
    )

    const { data, error } = useQuery<iNotificationsSettings>(
        ['profileNotificationSettings', id],
        async () => await getProfileNotificationSettings(id),
        {
            enabled: !profileSettings,
            refetchOnWindowFocus: false
        }
    )

    /*
     * Use query data immediately instead of waiting
     * one render for Zustand to be populated.
     * This removes the spinner -> empty -> populated flicker.
     */
    const resolvedProfileSettings = profileSettings ?? data

    useEffect(() => {
        if (data) {
            setProfileSettings(id, data)
        }
    }, [data, id, setProfileSettings])

    useEffect(() => {
        if (error) {
            addToast('error', t('error:errorSomethingWentWrong'))
        }
    }, [error, t])

    const updateNotificationMutation = useMutation(
        async (payload: UpdateNotificationPayload) =>
            await updateNotificationSettings(payload),
        {
            onError: err => {
                console.error('Error updating notification setting.', err)
                addToast(
                    'error',
                    t('error:errorWhileUpdatingNotificationSettings')
                )
            }
        }
    )

    const channelData = resolvedProfileSettings?.[activeTab] as
        | FlatChannelSettings
        | GroupedChannelSettings
        | undefined

    /*
     * Fan response:
     * push: { new_comment: true, ... }
     *
     * Model response:
     * push: {
     *   model: {...},
     *   subscriber: {...}
     * }
     *
     * Profile notifications concern this user's relationship
     * to the viewed profile, so models use subscriber settings.
     */
    const channelSettings: FlatChannelSettings =
        isGroupedChannelSettings(channelData)
            ? channelData.subscriber ?? {}
            : channelData ?? {}

    const globalSettingKey = GLOBAL_SETTING_KEYS[activeTab]

    const settingsDisabled = resolvedProfileSettings
        ? !resolvedProfileSettings.notificationSettings?.[globalSettingKey]
        : false

    const notificationEntries = Object.entries(channelSettings)

    const allEnabled =
        notificationEntries.length > 0 &&
        notificationEntries.every(([, value]) => Boolean(value))

    const toggleSetting = (
        key: string,
        currentValue: boolean
    ) => {
        const nextValue = !currentValue

        setProfileChannelSetting(
            id,
            activeTab,
            key,
            nextValue
        )

        updateNotificationMutation.mutate(
            {
                other_user_id: id,
                type: activeTab,
                [key]: nextValue
            },
            {
                onError: () => {
                    setProfileChannelSetting(
                        id,
                        activeTab,
                        key,
                        currentValue
                    )
                }
            }
        )
    }

    const toggleAll = () => {
        if (!notificationEntries.length) {
            return
        }

        const nextValue = !allEnabled
        const updates: Record<string, boolean> = {}

        notificationEntries.forEach(([key]) => {
            updates[key] = nextValue

            setProfileChannelSetting(
                id,
                activeTab,
                key,
                nextValue
            )
        })

        updateNotificationMutation.mutate({
            other_user_id: id,
            type: activeTab,
            ...updates
        })
    }

    const renderNotificationItems = () => (
        <div className='profileNotificationsModal__items'>
            <div className='profileNotificationsModal__item'>
                <p>{t('notifications:allNotifications')}</p>

                <SwitchButton
                    active={allEnabled}
                    toggle={toggleAll}
                />
            </div>

            {notificationEntries.map(([key, value]) => (
                <div
                    key={key}
                    className='profileNotificationsModal__item'
                    onClick={() => {
                        toggleSetting(key, Boolean(value))
                    }}
                >
                    <p>
                        {t(
                            NOTIFICATION_LABELS[key] ??
                            key
                        )}
                    </p>

                    <RadioButton
                        active={Boolean(value)}
                    />
                </div>
            ))}
        </div>
    )

    return (
        <div className='profileNotificationsModal'>
            <div className='profileNotificationsModal__nav'>
                {CHANNELS.map(channel => (
                    <div
                        key={channel}
                        className={`profileNotificationsModal__nav__item${
                            activeTab === channel
                                ? ' profileNotificationsModal__nav__item--active'
                                : ''
                        }`}
                        onClick={() => {
                            setActiveTab(channel)
                        }}
                    >
                        {t(CHANNEL_LABELS[channel])}
                    </div>
                ))}
            </div>

            {!resolvedProfileSettings ? (
                <div className='profileNotificationsModal__loading'>
                    <BeatLoader size={8} />
                </div>
            ) : settingsDisabled ? (
                <div className='profileNotificationsModal__disabled'>
                    <div className='profileNotificationsModal__disabled--text'>
                        {t('notificationCategoryIsDisabled')}
                        <br />
                        {t('enableItInSettings')}
                    </div>

                    {renderNotificationItems()}
                </div>
            ) : (
                renderNotificationItems()
            )}

            <div className='profileNotificationsModal__actions'>
                <Button
                    text={t('apply')}
                    color='black'
                    font='mont-14-normal'
                    width='13'
                    height='3'
                    clickFn={() => {
                        modalData.clearModal()
                    }}
                />
            </div>
        </div>
    )
}

export default ProfileNotificationsModal